import dayjs from 'dayjs';
import { 
  BusinessMetrics, 
  ClientAnalyticsData, 
  RevenueData, 
  PerformanceData,
  AnalyticsFilters,
  TrendData,
  ComparisonData 
} from '../types';
import { 
  PERFORMANCE_THRESHOLDS, 
  METRIC_COLORS, 
  STATUS_COLORS,
  TREND_DIRECTIONS,
  DATE_RANGES 
} from '../constants';

// Data filtering utilities
export const filterDataByDateRange = <T extends { date: string }>(
  data: T[], 
  dateRange: [string, string]
): T[] => {
  const [startDate, endDate] = dateRange;
  return data.filter(item => {
    const itemDate = dayjs(item.date);
    return itemDate.isAfter(dayjs(startDate)) && itemDate.isBefore(dayjs(endDate));
  });
};

export const applyAnalyticsFilters = <T>(
  data: T[], 
  filters: AnalyticsFilters,
  filterFunctions: { [key: string]: (item: T, filterValue: any) => boolean }
): T[] => {
  return data.filter(item => {
    // Apply date range filter
    if (filters.dateRange && filterFunctions.dateRange) {
      if (!filterFunctions.dateRange(item, filters.dateRange)) return false;
    }

    // Apply other filters
    for (const [key, value] of Object.entries(filters)) {
      if (key !== 'dateRange' && Array.isArray(value) && value.length > 0) {
        if (filterFunctions[key] && !filterFunctions[key](item, value)) {
          return false;
        }
      }
    }

    return true;
  });
};

// Calculation utilities
export const calculateGrowthRate = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

export const calculateChurnRate = (churnedClients: number, totalClients: number): number => {
  if (totalClients === 0) return 0;
  return (churnedClients / totalClients) * 100;
};

export const calculateRetentionRate = (retainedClients: number, totalClients: number): number => {
  if (totalClients === 0) return 0;
  return (retainedClients / totalClients) * 100;
};

export const calculateLifetimeValue = (
  avgRevenue: number, 
  avgLifespan: number, 
  retentionRate: number
): number => {
  if (retentionRate === 0) return avgRevenue;
  return (avgRevenue * avgLifespan) / (1 - (retentionRate / 100));
};

export const calculateMovingAverage = (data: number[], window: number): number[] => {
  const result: number[] = [];
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - window + 1);
    const subset = data.slice(start, i + 1);
    const average = subset.reduce((sum, val) => sum + val, 0) / subset.length;
    result.push(average);
  }
  return result;
};

// Trend analysis utilities
export const determineTrend = (current: number, previous: number): 'up' | 'down' | 'stable' => {
  const threshold = 0.05; // 5% threshold for stability
  const change = Math.abs((current - previous) / previous);
  
  if (change < threshold) return TREND_DIRECTIONS.STABLE;
  return current > previous ? TREND_DIRECTIONS.UP : TREND_DIRECTIONS.DOWN;
};

export const calculateTrendData = (
  data: { period: string; value: number }[]
): TrendData[] => {
  return data.map((item, index) => {
    const previousValue = index > 0 ? data[index - 1].value : item.value;
    const change = item.value - previousValue;
    const trend = determineTrend(item.value, previousValue);
    
    return {
      period: item.period,
      value: item.value,
      change,
      trend,
    };
  });
};

export const calculateComparison = (current: number, previous: number): ComparisonData => {
  const change = current - previous;
  const percentage = previous !== 0 ? (change / previous) * 100 : 0;
  
  return {
    current,
    previous,
    change,
    percentage,
  };
};

// Performance evaluation utilities
export const getPerformanceStatus = (
  value: number, 
  metric: keyof typeof PERFORMANCE_THRESHOLDS
): 'excellent' | 'good' | 'average' | 'poor' => {
  const thresholds = PERFORMANCE_THRESHOLDS[metric];
  
  if (value >= thresholds.EXCELLENT) return 'excellent';
  if (value >= thresholds.GOOD) return 'good';
  if (value >= thresholds.AVERAGE) return 'average';
  return 'poor';
};

export const getMetricColor = (metric: string): string => {
  return METRIC_COLORS[metric as keyof typeof METRIC_COLORS] || '#1890ff';
};

export const getStatusColor = (status: string): string => {
  return STATUS_COLORS[status.toUpperCase() as keyof typeof STATUS_COLORS] || '#d9d9d9';
};

// Formatting utilities
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

export const formatMetric = (value: number, type: string): string => {
  switch (type) {
    case 'currency':
      return formatCurrency(value);
    case 'percentage':
      return formatPercentage(value);
    case 'number':
      return formatNumber(value);
    case 'time':
      return `${value.toFixed(1)}h`;
    default:
      return value.toString();
  }
};

// Date utilities
export const getDateRangeForPeriod = (period: string): [string, string] => {
  const now = dayjs();
  
  switch (period) {
    case DATE_RANGES.LAST_7_DAYS:
      return [now.subtract(7, 'day').format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
    case DATE_RANGES.LAST_30_DAYS:
      return [now.subtract(30, 'day').format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
    case DATE_RANGES.LAST_90_DAYS:
      return [now.subtract(90, 'day').format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
    case DATE_RANGES.LAST_6_MONTHS:
      return [now.subtract(6, 'month').format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
    case DATE_RANGES.LAST_YEAR:
      return [now.subtract(1, 'year').format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
    case DATE_RANGES.CURRENT_MONTH:
      return [now.startOf('month').format('YYYY-MM-DD'), now.endOf('month').format('YYYY-MM-DD')];
    case DATE_RANGES.CURRENT_QUARTER:
      const quarterStart = now.startOf('month').subtract((now.month() % 3), 'month');
      const quarterEnd = quarterStart.add(2, 'month').endOf('month');
      return [quarterStart.format('YYYY-MM-DD'), quarterEnd.format('YYYY-MM-DD')];
    case DATE_RANGES.CURRENT_YEAR:
      return [now.startOf('year').format('YYYY-MM-DD'), now.endOf('year').format('YYYY-MM-DD')];
    default:
      return [now.subtract(30, 'day').format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
  }
};

// Data aggregation utilities
export const aggregateByPeriod = (
  data: { date: string; value: number }[],
  period: 'day' | 'week' | 'month' | 'quarter' | 'year'
): { period: string; value: number }[] => {
  const grouped = data.reduce((acc, item) => {
    const itemDate = dayjs(item.date);
    let key: string;
    
    if (period === 'quarter') {
      const quarterMonth = Math.floor(itemDate.month() / 3) * 3;
      key = itemDate.month(quarterMonth).startOf('month').format('YYYY-MM-DD');
    } else {
      key = itemDate.startOf(period as any).format('YYYY-MM-DD');
    }
    
    if (!acc[key]) {
      acc[key] = { total: 0, count: 0 };
    }
    acc[key].total += item.value;
    acc[key].count += 1;
    return acc;
  }, {} as { [key: string]: { total: number; count: number } });

  return Object.entries(grouped).map(([period, data]) => ({
    period,
    value: data.total,
  })).sort((a, b) => a.period.localeCompare(b.period));
};

// Export utilities
export const generateReportData = (
  businessMetrics: BusinessMetrics,
  clientAnalytics: ClientAnalyticsData,
  revenueData: RevenueData,
  performanceData: PerformanceData
) => {
  return {
    summary: {
      totalRevenue: revenueData.overview.totalRevenue,
      totalClients: businessMetrics.clients.totalClients,
      retentionRate: businessMetrics.clients.retentionRate,
      growthRate: businessMetrics.revenue.growthRate,
    },
    details: {
      revenue: revenueData,
      clients: clientAnalytics,
      performance: performanceData,
      metrics: businessMetrics,
    },
    generatedAt: dayjs().toISOString(),
  };
};

export const exportToCSV = (data: any[], filename: string): void => {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        return typeof value === 'string' ? `"${value}"` : value;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}; 